/**
 * Authentication Context
 * Manages Firebase Auth state and role-based access.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Listen to Firebase auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const idToken = await user.getIdToken(true);

          // Ask backend to resolve role/profile from Firestore as source of truth.
          try {
            const verifyResponse = await authAPI.verifyToken(idToken);
            if (verifyResponse.success) {
              const resolvedRole = verifyResponse?.data?.role || null;
              setUserRole(resolvedRole);
              setUserProfile(verifyResponse?.data?.profile || null);
            } else {
              const tokenResult = await user.getIdTokenResult();
              setUserRole(tokenResult.claims.role || null);
            }
          } catch (verifyErr) {
            // Fallback: claims + profile endpoint
            const tokenResult = await user.getIdTokenResult();
            setUserRole(tokenResult.claims.role || null);

            try {
              const response = await authAPI.getProfile();
              if (response.success) {
                setUserProfile(response.data);
              }
            } catch (profileErr) {
              console.warn('Could not fetch profile:', profileErr);
              setUserProfile(null);
            }
          }
        } catch (err) {
          console.error('Auth state error:', err);
          setError(err.message);
        }
      } else {
        setUserProfile(null);
        setUserRole(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign up with email/password
  const signup = async (email, password, displayName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign in
  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      setUserRole(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Password reset
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Register user profile in backend
  const registerProfile = async (profileData, userOverride = null) => {
    try {
      const authUser = userOverride || currentUser || auth.currentUser;
      if (!authUser) {
        throw new Error('Authentication state not ready. Please sign in and retry.');
      }

      const idToken = await authUser.getIdToken(true);
      const response = await authAPI.register(profileData, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (response.success) {
        // Refresh token to get new claims
        const freshToken = await authUser.getIdToken(true);

        try {
          const verifyResponse = await authAPI.verifyToken(freshToken);
          if (verifyResponse.success) {
            setUserRole(verifyResponse?.data?.role || profileData.role);
            setUserProfile(verifyResponse?.data?.profile || null);
          }
        } catch {
          const tokenResult = await authUser.getIdTokenResult();
          setUserRole(tokenResult.claims.role || profileData.role);

          try {
            const profileResponse = await authAPI.getProfile();
            if (profileResponse.success) {
              setUserProfile(profileResponse.data);
            }
          } catch {
            setUserProfile(response.data || null);
          }
        }
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Refresh user role from token
  const refreshRole = async () => {
    if (currentUser) {
      const tokenResult = await currentUser.getIdTokenResult(true);
      const role = tokenResult.claims.role || null;
      setUserRole(role);
      return role;
    }
    return null;
  };

  const value = {
    currentUser,
    userProfile,
    userRole,
    loading,
    error,
    signup,
    login,
    logout,
    resetPassword,
    registerProfile,
    refreshRole,
    setError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
